/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-restricted-syntax */
import { Mutex } from 'async-mutex';
import {
  LoginForm,
  NewProjectForm,
  NewReportForm,
  NewUserForm,
  NewScreenshotForm,
  RefreshTokenForm,
} from './schemas';
import {
  NewUser,
  Pagination,
  NewPassword,
  Project,
  Report,
  User,
  ForgotPassword,
  ResetPassword,
  Status,
  ReportStatus,
  ListParamsReport,
  ListParamsUser,
  UserTokenPayload,
  ListParamsProject,
  JwtTokens,
  Screenshot,
} from '../types';
import { ENDPOINTS } from './constants';
import { authAtom, AuthState } from '../atoms';
import { store } from './store';

const mutex = new Mutex();

export enum RequestMethods {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  PATCH = 'PATCH',
  DELETE = 'DELETE',
}

export const getAuthLocal = () => {
  const auth = store.get(authAtom);

  return auth;
};

const createQueryString = (params: object, prefix = '') => {
  const queryString = Object.keys(params)
    .map((key: string): string => {
      const value = params[key as keyof typeof params];
      const prefixedKey = prefix ? `${prefix}[${key}]` : key;

      if (typeof value === 'object' && value !== null) {
        return createQueryString(value as object, prefixedKey);
      }
      return `${encodeURIComponent(prefixedKey)}=${encodeURIComponent(value)}`;
    })
    .join('&');

  return queryString;
};

export const setAuthLocal = (value?: AuthState) => {
  const auth = store.set(authAtom, value);

  return auth;
};

export class ApiClient {
  async fetchApiWithRefetch<T>(params: {
    url: string;
    contentType?: string;
    headers?: object;
    formData?: object;
    method: RequestMethods;
    query?: string;
    body?: object;
    jsonCb?: (data: any) => T;
  }): Promise<T> {
    await mutex.waitForUnlock();

    try {
      const result = await this.fetchApi<T>(params);

      return result;
    } catch (error: any) {
      const isUnauthError = error.cause?.status && error.cause?.status === 401;
      const isServerUnavailableError = error.cause?.status && error.cause?.status === 503;

      if (isUnauthError) {
        const result = await mutex.runExclusive(async () => {
          const auth = getAuthLocal();

          if (!auth?.refreshToken) {
            await this.logout({ sendRequest: false });
            throw new Error(error);
          }

          const refreshResult = await this.fetchApi<JwtTokens>({
            url: ENDPOINTS.REFRESH,
            method: RequestMethods.POST,
            headers: {
              Authorization: `Bearer ${auth.refreshToken}`,
            },
          });

          setAuthLocal({
            ...auth,
            accessToken: refreshResult.accessToken,
            refreshToken: refreshResult.refreshToken,
          });

          const validationResult = await this.fetchApi<UserTokenPayload>({
            url: ENDPOINTS.VALIDATE,
            method: RequestMethods.GET,
          });

          setAuthLocal({
            ...getAuthLocal(),
            user: validationResult,
          });

          return this.fetchApi(params);
        });

        return result;
      }

      if (isServerUnavailableError) {
        await this.logout({ sendRequest: false });
      }

      throw new Error(error);
    }
  }

  // eslint-disable-next-line class-methods-use-this
  async fetchApi<T>({
    url,
    method,
    body,
    query,
    formData,
    jsonCb = (data) => data,
    headers = {},
  }: {
    url: string;
    formData?: Record<string, any>;
    headers?: object;
    method: RequestMethods;
    query?: string;
    body?: Record<string, any>;
    jsonCb?: (data: any) => T;
  }) {
    const auth = getAuthLocal();

    const reqFormData = new FormData();

    if (formData) {
      for (const key in formData) {
        if (formData[key]) reqFormData.append(key, formData[key]);
      }
    }

    const requestHeaders = new Headers({
      ...(formData ? {} : { 'Content-Type': 'application/json' }),
      'x-api-key': import.meta.env.VITE_API_KEY,
      ...(auth?.accessToken ? { Authorization: `Bearer ${auth.accessToken}` } : {}),
      ...headers,
    });

    const queryString = new URLSearchParams(query).toString();

    const baseUrl = (import.meta.env.VITE_BACKEND_URL as string) || '';

    let jsonData: any;
    let data: Response;
    try {
      data = await fetch(`${baseUrl}${url}?${queryString}`, {
        mode: 'cors',
        method,
        headers: requestHeaders,
        body: formData ? reqFormData : JSON.stringify(body),
      });

      jsonData = await data.json();

      if (data.ok) {
        return jsonCb(jsonData);
      }
    } catch (error: any) {
      throw new Error(error, {
        cause: {
          status: 503,
          statusText: error,
        },
      });
    }

    throw new Error(jsonData.message, {
      cause: { status: data.status, statusText: data.statusText },
    });
  }

  login = (props: LoginForm) => {
    return this.fetchApi<JwtTokens>({
      url: ENDPOINTS.LOGIN,
      method: RequestMethods.POST,
      body: props,
    });
  };

  refresh = (props: RefreshTokenForm) => {
    return this.fetchApiWithRefetch<JwtTokens>({
      method: RequestMethods.POST,
      url: ENDPOINTS.REFRESH,
      body: props,
      headers: {
        Authorization: `Bearer ${props.refreshToken}`,
      },
    });
  };

  validate = () => {
    return this.fetchApiWithRefetch<UserTokenPayload>({
      method: RequestMethods.GET,
      url: ENDPOINTS.VALIDATE,
    });
  };

  logout = async ({ sendRequest } = { sendRequest: true }) => {
    if (sendRequest) {
      try {
        await this.fetchApiWithRefetch({
          method: RequestMethods.POST,
          url: ENDPOINTS.LOGOUT,
        });
      } catch (error: any) {
        console.log(error);
      }
    }

    setAuthLocal({ user: null, accessToken: null, refreshToken: null });
  };

  getUserProjects = (query?: ListParamsProject) => {
    return this.fetchApiWithRefetch<{
      projects: Project[];
      pagination?: Pagination;
    }>({
      method: RequestMethods.GET,
      url: ENDPOINTS.PROJECT,
      query: query ? createQueryString(query) : undefined,
      jsonCb: ({ data, pagination }) => ({
        projects: data,
        pagination,
      }),
    });
  };

  updateProjectUsers = (params: { pid: string; users: string[] }) => {
    return this.fetchApiWithRefetch<{
      project: Project;
    }>({
      method: RequestMethods.PATCH,
      url: ENDPOINTS.PROJECT_USERS(params.pid),
      body: { users: params.users },
      jsonCb: (data) => ({
        project: data,
      }),
    });
  };

  getUserById = (uid: string) => {
    return this.fetchApiWithRefetch<{ user: User }>({
      method: RequestMethods.GET,
      url: `${ENDPOINTS.USER}/${uid}`,
      jsonCb: (data) => ({ user: data }),
    });
  };

  updateLanguage = (language: string) => {
    return this.fetchApiWithRefetch({
      method: RequestMethods.PATCH,
      url: ENDPOINTS.UPDATE_LANGUAGE,
      body: { language },
    });
  };

  getReportsByProject = (query: ListParamsReport) => {
    return this.fetchApiWithRefetch<{
      reports: Report[];
      pagination?: Pagination;
    }>({
      method: RequestMethods.GET,
      url: ENDPOINTS.REPORT,
      query: createQueryString(query),
      jsonCb: ({ data, pagination }) => ({
        reports: data,
        pagination,
      }),
    });
  };

  getReportById = (id: string) => {
    return this.fetchApiWithRefetch<{ report: Report }>({
      method: RequestMethods.GET,
      url: `${ENDPOINTS.REPORT}/${id}`,
      jsonCb: (data) => ({
        report: data,
      }),
    });
  };

  changeReportStatus = (id: string, status: ReportStatus) => {
    return this.fetchApiWithRefetch<{ report: Report }>({
      method: RequestMethods.PATCH,
      url: `${ENDPOINTS.REPORT}/${id}/status`,
      body: { status },
      jsonCb: (data) => ({
        report: data,
      }),
    });
  };

  createReport = (
    report: Omit<Partial<NewReportForm>, 'expectedScreenshots' | 'currentScreenshots'> & {
      project: string;
      expectedScreenshots: string[];
      currentScreenshots: string[];
    },
  ) => {
    return this.fetchApiWithRefetch<{ report: Report }>({
      method: RequestMethods.POST,
      url: ENDPOINTS.REPORT,
      body: report,
      jsonCb: (data) => ({
        report: data,
      }),
    });
  };

  createScreenshot = (screenshot: Partial<NewScreenshotForm>) => {
    return this.fetchApiWithRefetch<{ screenshot: Screenshot }>({
      method: RequestMethods.POST,
      url: ENDPOINTS.SCREENSHOT,
      formData: screenshot,
      jsonCb: (data) => ({
        screenshot: data,
      }),
    });
  };

  deleteReport = (id: string) => {
    return this.fetchApiWithRefetch({
      method: RequestMethods.DELETE,
      url: `${ENDPOINTS.REFRESH}/${id}`,
    });
  };

  registrateUser = (user: Omit<NewUserForm, 'project'> & { project: string[] }) => {
    return this.fetchApiWithRefetch<{ user: NewUser }>({
      method: RequestMethods.POST,
      url: ENDPOINTS.SIGNUP,
      body: user,
      jsonCb: (data) => ({
        user: data,
      }),
    });
  };

  getUsers = (query: ListParamsUser) => {
    return this.fetchApiWithRefetch<{
      users: User[];
      pagination?: Pagination;
    }>({
      method: RequestMethods.GET,
      url: ENDPOINTS.USER,
      query: createQueryString(query),
      jsonCb: ({ data, pagination }) => ({
        users: data,
        pagination,
      }),
    });
  };

  createProject = (project: Omit<NewProjectForm, 'users'> & { users: string[] }) => {
    return this.fetchApiWithRefetch<{ project: Project }>({
      method: RequestMethods.POST,
      url: ENDPOINTS.PROJECT,
      body: project,
      jsonCb: (data) => ({
        project: data,
      }),
    });
  };

  changeProjectStatus = (project: Project, status: string) => {
    return this.fetchApiWithRefetch<{ project: Project }>({
      method: RequestMethods.PATCH,
      url: `${ENDPOINTS.PROJECT}/${project.id}`,
      body: { status },
      jsonCb: (data) => ({
        project: data,
      }),
    });
  };

  getProjectById = (pid: string) => {
    return this.fetchApiWithRefetch<{ project: Project }>({
      method: RequestMethods.GET,
      url: `${ENDPOINTS.PROJECT}/${pid}`,
      jsonCb: (data) => ({
        project: data,
      }),
    });
  };

  getStatusesByProjectId = (pid: string) => {
    return this.fetchApiWithRefetch<{ statuses: Status[] }>({
      method: RequestMethods.GET,
      url: `${ENDPOINTS.PROJECT}/${pid}/status`,
      jsonCb: (data) => ({
        statuses: data,
      }),
    });
  };

  changePassword = (dto: NewPassword) => {
    return this.fetchApiWithRefetch({
      method: RequestMethods.POST,
      url: ENDPOINTS.PASSWORD,
      body: dto,
    });
  };

  forgotPassword = (dto: ForgotPassword) => {
    return this.fetchApiWithRefetch({
      method: RequestMethods.POST,
      url: ENDPOINTS.FORGOT_PASSWORD,
      body: dto,
    });
  };

  resetPassword = (dto: ResetPassword) => {
    return this.fetchApiWithRefetch({
      method: RequestMethods.POST,
      url: ENDPOINTS.RESET_PASSWORD,
      body: dto,
      headers: {
        Authorization: `Bearer ${dto.token}`,
      },
    });
  };
}
